import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { callbackUrl } from '../../api/util';
import AuthenticationErrorMessage from '../../components/AuthenticationErrorMessage/AuthenticationErrorMessage';
import { PARAM_AUTH_REQ_ID, PARAM_CODE, PARAM_ERROR, PARAM_STATE } from '../../components/AuthenticationWrapper';
import Description from '../../components/Description/Description';
import { ErrorMessage } from '../../components/ErrorMessage';
import GovernmentIdField from '../../components/GovernmentIdField/GovernmentIdField';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { ConsumerLoanTargetPath } from '../../domain/types';
import { extractTokenClaim } from '../../helpers/jwt';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useSettings from '../../hooks/useSettings';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldStateByName } from '../../store/applicationform/applicationFormSelectors';
import {
    authenticationStateConsumed,
    getAccessToken,
    initAuthentication,
    selectAuthenticationState,
    selectValidAccessToken,
} from '../../store/authentication';
import { setGovernmentIdPageIndex, WizardPageAction } from '../../store/wizard/wizard';
import { AttributeNames } from '../../types/AttributeNames';

const JWT_CLAIM_SBL_PUBLIC_ID = 'publicId';
const APPLICANT_SBL_REFERENCE_ID = 'applicantSblReferenceId';
const APPLICANT_SBL_FAILED = 'applicantSblFailed';

const VerifyIncomePageNO = () => {
    const { countryCode, languageCode } = useSettings();
    const translate = useTranslation();
    const { totalSections, totalAboutYouPages } = usePagesConfig();
    const dispatch = useAppDispatch();
    const applicantSblReferenceFieldState = useAppSelector(selectFieldStateByName)(APPLICANT_SBL_REFERENCE_ID);
    const applicantSblFailedFieldState = useAppSelector(selectFieldStateByName)(APPLICANT_SBL_FAILED);
    const applicantGovernmentIdFieldState = useAppSelector(selectFieldStateByName)(AttributeNames.applicantGovernmentId);
    const { applicationId } = useAppSelector((state) => state.application);
    const [params, setParams] = useSearchParams();
    const accessToken = useAppSelector(selectValidAccessToken);
    const authenticationState = useAppSelector(selectAuthenticationState);
    const { currentPage, pageAction } = usePagesContext();
    const defaultPagesAction = useDefaultPagesAction();
    const validationErrors = useValidation([AttributeNames.applicantGovernmentId]);
    const [error, setError] = useState<boolean>(false);
    useComponentTracker('VerifyIncomePage');

    useSetBannerType('bannerOne');

    useEffect(() => {
        // Record where the government-id collection page sits in the current wizard (NO or SE) so SubmitPage
        // can route the user back here if the id is missing, without hard-coding a page index (LP-2699).
        dispatch(setGovernmentIdPageIndex(currentPage));
    }, [dispatch, currentPage]);

    const dispatchGetAccessToken = () =>
        dispatch(
            getAccessToken({
                state: params.get(PARAM_STATE)!,
                code: params.get(PARAM_CODE)!,
            }),
        );

    useEffect(() => {
        // Auto-advance only on a genuine SBL success. A genuine failure (applicantSblFailed) deliberately does
        // NOT auto-advance here -- the applicant needs to actually see the "continuing without automatic
        // verification" message below and make an explicit choice (retry or continue), not have it flash past
        // in the same instant the redirect resolves. Checking value alone is not enough: the field is written
        // on every keystroke, so a truthiness-only guard would skip mid-typing the moment the first character
        // is entered (and let a partial/invalid value through). Requiring !hasError means we only advance once
        // the id is complete and format-valid. Without this the page would silently skip an empty
        // applicantGovernmentId (it is only validated here), letting an empty value reach /application/submit
        // and NPE the backend (LP-2699).
        const canAutoAdvance =
            !!applicantSblReferenceFieldState?.value &&
            !!applicantGovernmentIdFieldState?.value &&
            !applicantGovernmentIdFieldState?.hasError;

        if (pageAction === 'back' && canAutoAdvance) {
            defaultPagesAction(pageAction);
        }

        if (pageAction === 'next' && canAutoAdvance) {
            defaultPagesAction(pageAction);
        }
    }, [
        pageAction,
        applicantSblReferenceFieldState?.value,
        applicantGovernmentIdFieldState?.value,
        applicantGovernmentIdFieldState?.hasError,
    ]);

    // A genuine SBL failure cancels the OAuth grant before a `code` is ever issued -- the auth server redirects
    // back with `?error=access_denied&state=...` instead. Requiring `state` to match the value this session's
    // own initAuthentication call generated (persisted in the authentication slice across the full-page BankID
    // redirect) is what the success path already relies on for its own getAccessToken.state check -- without
    // it, a bare `?error=access_denied` in the URL bar, with no BankID attempt ever made, would be enough to
    // mark SBL as failed and unlock continuing with a manually-typed, unverified government id. Matching only
    // 'access_denied' (not any truthy error value) also keeps unrelated OAuth errors from being silently
    // swallowed into this UX.
    const isGenuineSblFailureRedirect =
        params.get(PARAM_ERROR) === 'access_denied' &&
        !!params.get(PARAM_STATE) &&
        params.get(PARAM_STATE) === authenticationState;

    useEffect(() => {
        if (accessToken) {
            return;
        }

        if (params.get(PARAM_CODE) && params.get(PARAM_STATE)) {
            dispatchGetAccessToken();
            params.delete(PARAM_CODE);
            params.delete(PARAM_STATE);
            params.delete(PARAM_AUTH_REQ_ID);
            setParams(params);
        } else if (isGenuineSblFailureRedirect) {
            dispatch(addOrUpdateFormField({ fieldName: APPLICANT_SBL_FAILED, value: 'true' }));
            // Clear the correlated state now that it's been consumed, so this exact error+state URL can't be
            // replayed later to re-trigger this branch from a stale, persisted authenticationState.
            dispatch(authenticationStateConsumed());
            params.delete(PARAM_ERROR);
            params.delete(PARAM_STATE);
            params.delete(PARAM_AUTH_REQ_ID);
            setParams(params);
        }
    }, [params, accessToken, isGenuineSblFailureRedirect]);

    useEffect(() => {
        // We don't have a login so we can't extract anything, or we've already resolved this attempt (either a
        // reference was found, or we've already recorded that SBL genuinely failed for this token).
        if (!accessToken || applicantSblReferenceFieldState?.value || applicantSblFailedFieldState?.value) {
            return;
        }
        const sblPublicId = extractTokenClaim(accessToken, JWT_CLAIM_SBL_PUBLIC_ID);
        if (sblPublicId) {
            dispatch(addOrUpdateFormField({ fieldName: APPLICANT_SBL_REFERENCE_ID, value: sblPublicId }));
        } else {
            // BankID succeeded but the token carries no SBL consent -- SBL itself failed. Record it distinctly
            // from "never attempted" so the applicant can still continue instead of being stuck here forever.
            dispatch(addOrUpdateFormField({ fieldName: APPLICANT_SBL_FAILED, value: 'true' }));
        }
    }, [accessToken, applicationId, applicantSblReferenceFieldState, applicantSblFailedFieldState]);

    const onAction = async (action: WizardPageAction) => {
        if (action === 'collect-details') {
            if (validationErrors.length) {
                scrollToFieldWithError(validationErrors);
                validationErrors.forEach((validationError) => {
                    dispatch(addOrUpdateFormField(validationError));
                });
                return false;
            }
            try {
                setError(false);
                // Clear a prior failure before retrying, so the upcoming fresh token gets re-evaluated instead
                // of being skipped by the "already resolved this attempt" guard above.
                dispatch(addOrUpdateFormField({ fieldName: APPLICANT_SBL_FAILED, value: '' }));
                await dispatch(
                    initAuthentication(applicantGovernmentIdFieldState?.value + '', {
                        scope: 'cl-sbl-NO',
                        redirectUrl: callbackUrl(ConsumerLoanTargetPath.application, languageCode, countryCode, ''),
                    }),
                );
                await new Promise((resolve) => setTimeout(resolve, 5000));
            } catch (error) {
                setError(true);
            }
            return false;
        }
        return defaultPagesAction(action);
    };

    // 'next' bypasses useValidation entirely (see useDefaultPagesAction), so only expose it once the
    // government id is actually present and error-free -- otherwise a failed-then-cleared/invalid id could
    // reach submit unvalidated.
    const canContinuePastSblFailure =
        applicantSblFailedFieldState?.value === 'true' &&
        !!applicantGovernmentIdFieldState?.value &&
        !applicantGovernmentIdFieldState?.hasError;
    const footerActions: WizardPageAction[] = canContinuePastSblFailure
        ? ['collect-details', 'next']
        : ['collect-details'];

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={onAction}
                currentPage={currentPage}
                currentSection={0}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
            <Title text={translate('page.verify-income.title')} />
            <Description text={translate('page.verify-income.description')} />
            <GovernmentIdField
                name={AttributeNames.applicantGovernmentId}
                countrycode={countryCode}
                label={translate('field.applicant-government-id.label')}
            />
            {error && (
                <ErrorMessage title={translate('page.error.title')} description={translate('page.error.description')} />
            )}
            {applicantSblFailedFieldState?.value === 'true' && (
                <ErrorMessage
                    title={translate('page.verify-income.sbl-failed.title')}
                    description={translate('page.verify-income.sbl-failed.description')}
                />
            )}
            {/* Suppressed once we already know (or, on this very render, are about to record) a genuine SBL
                failure -- otherwise this reads the same `error` param synchronously and would show the old
                generic "login failed" banner for one paint before the effect above replaces it. */}
            {!(applicantSblFailedFieldState?.value === 'true' || isGenuineSblFailureRedirect) && (
                <AuthenticationErrorMessage />
            )}
            <PagesFooter
                actions={footerActions}
                onAction={onAction}
                currentSection={0}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
        </>
    );
};

export default VerifyIncomePageNO;
